// eslint-disable-next-line no-restricted-imports
import moment, {MomentInput} from 'moment';

import {getLocalizationService} from '~/shared/services/localisationService';

import {DEFAULT_LANGUAGE_KEY} from '../consts/commonConsts';
import {FutureOrderAvailableDatesAndTimes} from '../store/models';

import {capitalize, isCurrentTimeKeyAsap} from './general';

const parseDotNetDate = (dateString: string): number | null => {
  if (typeof dateString === 'string' && dateString.startsWith('/Date(') && dateString.endsWith(')/')) {
    const match = dateString.match(/\/Date\((\d+)\)\//);
    return match ? parseInt(match[1], 10) : null;
  }
  return null;
};

export const getParsedDateDiff = (startDate?: MomentInput): string | undefined => {
  if (!startDate) {
    return;
  }

  const dotNetTimestamp = parseDotNetDate(startDate as string);
  const start = dotNetTimestamp ? moment(dotNetTimestamp) : moment(startDate);

  if (!start.isValid()) {
    return;
  }

  const {currentLanguageKey, t} = getLocalizationService();
  const now = moment().locale(currentLanguageKey);
  const isFutureDate = start.isAfter(now);

  if (now.isSame(start, 'day')) {
    return t('today');
  }
  
  if (!isFutureDate && start.isSame(now.clone().subtract(1, 'day'), 'day')) {
    return t('yesterday');
  }
  
  if (isFutureDate && start.isSame(now.clone().add(1, 'day'), 'day')) {
    return t('for_tomorrow');
  }

  const diffInDays = Math.abs(start.diff(now, 'days'));
  const diffInWeeks = Math.abs(start.diff(now, 'weeks'));
  const diffInMonths = Math.abs(start.diff(now, 'months'));

  if (diffInMonths > 0) {
    if (diffInMonths === 1) {
      return t(isFutureDate ? 'one_month_ahead' : 'one_month_ago');
    }
    if (diffInMonths === 2) {
      return t(isFutureDate ? 'two_months_ahead' : 'two_months_ago');
    }
    return t(isFutureDate ? 'x_months_ahead' : 'x_months_ago', {numberOfMonths: diffInMonths});
  }

  if (diffInWeeks > 0) {
    if (diffInWeeks === 1) {
      return t(isFutureDate ? 'a_week_ahead' : 'a_week_ago');
    }
    if (diffInWeeks === 2) {
      return t(isFutureDate ? 'two_weeks_ahead' : 'two_weeks_ago');
    }
    if (diffInWeeks === 4) {
      return t(isFutureDate ? 'one_month_ahead' : 'one_month_ago');
    }
    return t(isFutureDate ? 'x_weeks_ahead' : 'x_weeks_ago', {numberOfWeeks: diffInWeeks});
  }

  if (diffInDays === 2) {
    return t(isFutureDate ? 'two_days_ahead' : 'two_days_ago');
  }
  
  if (diffInDays > 0) {
    return t(isFutureDate ? 'x_days_ahead' : 'x_days_ago', {numberOfDays: diffInDays});
  }

  return undefined;
};

export const getArrivalText = (arrivalOrderDate: MomentInput, momentFormat?: string): string | undefined => {
  const {currentLanguageKey, t} = getLocalizationService();

  const now = moment();
  const givenDate = moment(arrivalOrderDate, momentFormat).locale(currentLanguageKey);

  if (now.isSame(givenDate, 'days')) {
    return capitalize(t('today'));
  }

  if (now.add(1, 'days').isSame(givenDate, 'days')) {
    return capitalize(t('tomorrow'));
  }

  const letterOfTheDay = givenDate.format('ddd');
  // 'ddd' actually returns the with `'` around the day name

  if (currentLanguageKey === DEFAULT_LANGUAGE_KEY) {
    return `יום ${letterOfTheDay}, ${arrivalOrderDate}`;
  }

  return `${letterOfTheDay}, ${arrivalOrderDate}`;
};

type SplitDate = {day: string; month: string; year: string};
export const getSplitStringDate = (sourceDate: string): SplitDate | undefined => {
  if (!sourceDate || sourceDate.split('/').length < 2) {
    return;
  }
  const [day, month, year] = sourceDate.split('/');
  return {day, month, year};
};

export const getDateTimeString = (
  orderTime?: {timeKey: string},
  orderDate?: FutureOrderAvailableDatesAndTimes['date'],
) => {
  if (!orderTime || !orderDate) {
    return;
  }

  const {day, month, year} = getSplitStringDate(orderDate) || {};
  const time = isCurrentTimeKeyAsap(orderTime.timeKey) ? moment().format('hh:mm') : orderTime.timeKey;
  return `${year}-${month}-${day}T${time}`;
};
